import {
  BEEF_AI_ACCESS_TOKEN,
  BEEF_AI_BASE_URL,
  beefAiGet,
  beefAiPatch,
} from "../common/utils/beefAiClient";
import { buildOmittedAssetUrl } from "./priceHistoryOmitted";

export const LOCATION_MISSING_DEFAULT_DAYS = 7;
export const LOCATION_MISSING_MAX_UNFILTERED_FETCH = 500;

const PRICE_HISTORY_FIELDS = [
  "id",
  "product_name",
  "retailer",
  "retailer_ref_id",
  "province",
  "metadata",
  "images",
  "date_created",
  "record_at",
];

export function locationMissingReportConfigured() {
  return Boolean(BEEF_AI_BASE_URL && BEEF_AI_ACCESS_TOKEN);
}

export function getDefaultLocationMissingDateFrom(days = LOCATION_MISSING_DEFAULT_DAYS) {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  return cutoff.toISOString();
}

function normalizeMetadata(metadata) {
  if (!metadata) return null;
  if (typeof metadata === "string") {
    try {
      const parsed = JSON.parse(metadata);
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch (_) {
      return null;
    }
  }
  return typeof metadata === "object" ? metadata : null;
}

export function extractWorkflowRunId(metadata) {
  const meta = normalizeMetadata(metadata);
  const runId = meta?.run;
  return typeof runId === "string" && runId.trim() ? runId.trim() : "";
}

export function isMissingGpsFromMetadata(metadata) {
  const meta = normalizeMetadata(metadata);
  const gpsPresent = meta?.exif_resolution?.gps_present;
  return gpsPresent !== true && gpsPresent !== 1 && gpsPresent !== "true" && gpsPresent !== "1";
}

/**
 * @param {{ retailer?: string, retailerRefId?: string, province?: string }} values
 */
export function buildPriceHistoryLocationPatchPayload(values = {}) {
  const payload = {};

  if (values.retailer !== undefined) {
    payload.retailer = values.retailer;
  }
  if (values.retailerRefId !== undefined) {
    payload.retailer_ref_id = values.retailerRefId;
  }
  if (values.province !== undefined) {
    payload.province = values.province;
  }

  if (!Object.keys(payload).length) {
    throw new Error("At least one of retailer or province must be provided.");
  }

  return payload;
}

/**
 * Dev-only preview of the Beef AI PATCH requests used by bulk location updates.
 *
 * @param {string[]} recordIds
 * @param {{ retailer?: string, retailerRefId?: string, province?: string }} values
 */
export function getLocationMissingBulkUpdateRequestPreview(recordIds = [], values = {}) {
  const ids = Array.from(new Set(recordIds.filter(Boolean)));
  if (!ids.length) {
    return null;
  }

  const url = `${BEEF_AI_BASE_URL}/items/price_history/${ids[0]}${
    ids.length > 1 ? ` (+${ids.length - 1} more)` : ""
  }`;

  try {
    return {
      method: "PATCH",
      url,
      payload: buildPriceHistoryLocationPatchPayload(values),
    };
  } catch (error) {
    return {
      method: "PATCH",
      url,
      payload: {},
      blockedReason:
        error?.message || "Choose a retailer and/or province before this request can be sent.",
    };
  }
}

function formatUploaderName(uploadedBy) {
  if (!uploadedBy || typeof uploadedBy === "string") {
    return "";
  }

  return `${uploadedBy.first_name || ""} ${uploadedBy.last_name || ""}`.trim();
}

function buildLocationMissingQueryParams({ page = 1, limit = 25, dateFrom, sort = "-date_created" } = {}) {
  const params = new URLSearchParams();
  params.set("access_token", BEEF_AI_ACCESS_TOKEN);
  params.set("page", String(page));
  params.set("limit", String(limit));
  params.set("sort", sort);

  PRICE_HISTORY_FIELDS.forEach((field) => {
    params.append("fields[]", field);
  });

  if (dateFrom) {
    params.set("filter[date_created][_gte]", dateFrom);
  }

  // `metadata` is a JSON column — nested paths like exif_resolution.gps_present are not
  // filterable as Directus fields. Missing-GPS filtering happens client-side instead.
  params.set("filter[_and][0][_or][0][is_void][_null]", "true");
  params.set("filter[_and][0][_or][1][is_void][_eq]", "false");

  return params;
}

async function fetchCandidatePriceHistoryRows(dateFrom) {
  const params = buildLocationMissingQueryParams({
    page: 1,
    limit: LOCATION_MISSING_MAX_UNFILTERED_FETCH,
    dateFrom,
  });
  const json = await beefAiGet(`/items/price_history?${params.toString()}`);
  return (json?.data || []).filter((row) => isMissingGpsFromMetadata(row?.metadata));
}

function paginateRows(rows, page, limit) {
  const start = (page - 1) * limit;
  return rows.slice(start, start + limit);
}

async function fetchSourceRunsByRunIds(runIds = []) {
  const uniqueRunIds = Array.from(new Set(runIds.filter(Boolean)));
  if (!uniqueRunIds.length) {
    return [];
  }

  const params = new URLSearchParams();
  params.set("access_token", BEEF_AI_ACCESS_TOKEN);
  params.set("limit", "-1");
  params.set("filter[run_id][_in]", uniqueRunIds.join(","));
  params.append("fields[]", "id");
  params.append("fields[]", "run_id");
  params.append("fields[]", "file_id.id");
  params.append("fields[]", "file_id.filename_download");
  params.append("fields[]", "file_id.uploaded_on");
  params.append("fields[]", "file_id.uploaded_by.first_name");
  params.append("fields[]", "file_id.uploaded_by.last_name");
  params.append("fields[]", "file_id.uploaded_by.email");

  const json = await beefAiGet(`/items/source_run?${params.toString()}`);
  return json?.data || [];
}

function resolveSourceRunFile(sourceRunRow) {
  const fileIdValue = sourceRunRow?.file_id;
  if (!fileIdValue) {
    return null;
  }

  if (typeof fileIdValue === "string") {
    return {
      id: fileIdValue,
      filename_download: "",
      uploaded_on: null,
      uploaded_by: null,
    };
  }

  return fileIdValue;
}

export function normalizeLocationMissingRow(row, sourceRunByRunId = new Map()) {
  const workflowRunId = extractWorkflowRunId(row?.metadata);
  const sourceRun = workflowRunId ? sourceRunByRunId.get(workflowRunId) : null;
  const sourceFile = resolveSourceRunFile(sourceRun);
  const uploadedBy = sourceFile?.uploaded_by;
  const uploaderName = formatUploaderName(uploadedBy);
  const uploaderEmail = typeof uploadedBy === "object" && uploadedBy ? uploadedBy.email || "" : "";
  const uploaderKey = uploaderEmail || uploaderName || "";

  return {
    id: row.id,
    productName: row.product_name || "",
    retailer: row.retailer || "",
    retailerRefId: row.retailer_ref_id || "",
    province: row.province || "",
    dateCreated: row.date_created || "",
    recordAt: row.record_at || "",
    workflowRunId,
    sourceFileId: sourceFile?.id || "",
    sourceFilename: sourceFile?.filename_download || "",
    sourceImageUrl: sourceFile?.id ? buildOmittedAssetUrl(sourceFile.id) : "",
    uploadedOn: sourceFile?.uploaded_on || "",
    uploaderName,
    uploaderEmail,
    uploaderKey,
    uploaderLabel: uploaderName
      ? uploaderEmail
        ? `${uploaderName} (${uploaderEmail})`
        : uploaderName
      : uploaderEmail || "-",
    hasVerifiedSource: Boolean(sourceFile?.id),
  };
}

export async function enrichLocationMissingRows(rows = []) {
  const runIds = rows.map((row) => extractWorkflowRunId(row?.metadata)).filter(Boolean);
  const sourceRuns = await fetchSourceRunsByRunIds(runIds);
  const sourceRunByRunId = new Map(
    sourceRuns.map((entry) => [String(entry.run_id || ""), entry]).filter(([runId]) => runId)
  );

  return rows.map((row) => normalizeLocationMissingRow(row, sourceRunByRunId));
}

export async function fetchLocationMissingRows({
  page = 1,
  limit = 25,
  dateFrom = getDefaultLocationMissingDateFrom(),
  uploadedByKey = "",
} = {}) {
  const candidates = await fetchCandidatePriceHistoryRows(dateFrom);
  const enriched = await enrichLocationMissingRows(candidates);
  const filtered = uploadedByKey
    ? enriched.filter((row) => row.uploaderKey === uploadedByKey)
    : enriched;

  return {
    rows: paginateRows(filtered, page, limit),
    total: filtered.length,
    uploaderOptions: buildUploaderOptions(enriched),
  };
}

export async function fetchLocationMissingUploaderOptions({
  dateFrom = getDefaultLocationMissingDateFrom(),
} = {}) {
  const candidates = await fetchCandidatePriceHistoryRows(dateFrom);
  const enriched = await enrichLocationMissingRows(candidates);
  return buildUploaderOptions(enriched);
}

function buildUploaderOptions(rows = []) {
  const byKey = new Map();

  rows.forEach((row) => {
    if (!row.uploaderKey) {
      return;
    }

    byKey.set(row.uploaderKey, {
      value: row.uploaderKey,
      label: row.uploaderLabel,
    });
  });

  return Array.from(byKey.values()).sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * @param {string} recordId
 * @param {{ retailer?: string, retailerRefId?: string, province?: string }} values
 */
export async function updatePriceHistoryLocationFields(recordId, values = {}) {
  if (!recordId) {
    throw new Error("Price history record id is required.");
  }

  const payload = buildPriceHistoryLocationPatchPayload(values);
  const json = await beefAiPatch(`/items/price_history/${recordId}`, payload);
  return json?.data || null;
}

/**
 * @param {string[]} recordIds
 * @param {{ retailer?: string, retailerRefId?: string, province?: string }} values
 */
export async function bulkUpdatePriceHistoryLocationFields(recordIds = [], values = {}) {
  const ids = Array.from(new Set(recordIds.filter(Boolean)));
  if (!ids.length) {
    throw new Error("Select at least one record to update.");
  }

  const results = [];
  for (const recordId of ids) {
    const updated = await updatePriceHistoryLocationFields(recordId, values);
    results.push(updated);
  }

  return results;
}
